// components/AIChatbot/MessageList.js

import React, { useEffect, useRef } from "react";
import { Box, Avatar, Typography, makeStyles } from "@material-ui/core";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import TypingIndicator from "./TypingIndicator";
import { useStore } from "../../context/StoreContext";
import { stringAvatar } from "../shared/helpers/helper";
import { IntakeMessage } from "../../types/AIChatBotTypes";
import { tokens } from "../../theme/tokens";

const useStyles = makeStyles(() => ({
  messageContainer: {
    display: "flex",
    marginBottom: "14px",
    alignItems: "flex-start",
  },
  userMessage: {
    flexDirection: "row-reverse",
  },
  avatar: {
    width: 30,
    height: 30,
    marginRight: 8,
    backgroundColor: tokens.color.text.secondary,
    fontSize: "0.75em",
  },
  userAvatar: {
    marginLeft: 8,
    marginRight: 0,
  },
  messageBubble: {
    maxWidth: "75%",
    padding: "9px 13px",
    borderRadius: "14px",
    wordWrap: "break-word",
  },
  botBubble: {
    backgroundColor: tokens.color.surface.sunken,
    color: tokens.color.text.primary,
    borderBottomLeftRadius: "4px",
  },
  userBubble: {
    backgroundColor: tokens.color.brand.primaryAction,
    color: tokens.color.brand.onPrimary,
    borderBottomRightRadius: "4px",
  },
  messageText: {
    fontSize: "0.825em",
    fontFamily: tokens.type.family,
    lineHeight: 1.5,
    "& p": {
      margin: "0 0 6px 0",
      "&:last-child": { marginBottom: 0 },
    },
    "& ul, & ol": {
      margin: "4px 0 6px 0",
      paddingLeft: "20px",
      "&:last-child": { marginBottom: 0 },
    },
    "& li": {
      marginBottom: "2px",
    },
    "& strong": {
      fontWeight: 600,
    },
  },
  timestamp: {
    fontSize: "0.7em",
    marginTop: "5px",
  },
  botTimestampColor: {
    color: tokens.color.text.secondary,
  },
  userTimestampColor: {
    color: tokens.color.brand.onPrimary,
    opacity: 0.6,
  },
}));

// WhatsApp-style timestamp: "2:30 PM", "Yesterday 2:30 PM", "Feb 17 2:30 PM"
const formatTimestamp = (created_at: number): string => {
  const date = new Date(created_at * 1000);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  const timeStr = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (date.toDateString() === now.toDateString()) {
    return timeStr;
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${timeStr}`;
  }
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} ${timeStr}`;
};

// Render inline markdown: **bold**
const renderInline = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

// Render block-level markdown: paragraphs, bullet lists, numbered lists
const renderMarkdown = (text: string): React.ReactNode => {
  const blocks = text.split(/\n\n+/);

  return (
    <>
      {blocks.map((block, blockIdx) => {
        const lines = block.split("\n").filter((l) => l !== "");
        if (lines.length === 0) return null;

        // Ordered list
        if (lines.every((l) => /^\d+\.\s/.test(l))) {
          return (
            <ol key={blockIdx}>
              {lines.map((line, i) => (
                <li key={i}>{renderInline(line.replace(/^\d+\.\s/, ""))}</li>
              ))}
            </ol>
          );
        }

        // Unordered list
        if (lines.every((l) => /^[-*]\s/.test(l))) {
          return (
            <ul key={blockIdx}>
              {lines.map((line, i) => (
                <li key={i}>{renderInline(line.replace(/^[-*]\s/, ""))}</li>
              ))}
            </ul>
          );
        }

        // Paragraph with optional inline line breaks
        return (
          <p key={blockIdx}>
            {lines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {renderInline(line)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
};

interface MessageListProps {
  messages: IntakeMessage[];
  isTyping: boolean;
  userFullName: string;
}

/**
 * Message List Component
 * Displays conversation history with auto-scroll
 */
const MessageList = ({
  messages,
  isTyping,
  userFullName,
}: MessageListProps) => {
  const classes = useStyles();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { avatarColor } = useStore();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  return (
    <Box>
      {messages.map((message, index) => (
        <Box
          key={index}
          className={`${classes.messageContainer} ${
            message.role === "user" ? classes.userMessage : ""
          }`}
        >
          <Avatar
            className={`${classes.avatar} ${
              message.role === "user" ? classes.userAvatar : ""
            }`}
            style={
              message.role === "user" && avatarColor
                ? { backgroundColor: avatarColor }
                : undefined
            }
          >
            {message.role === "assistant" ? (
              <SmartToyIcon />
            ) : (
              <>{stringAvatar(userFullName)}</>
            )}
          </Avatar>

          <Box
            className={`${classes.messageBubble} ${
              message.role === "assistant"
                ? classes.botBubble
                : classes.userBubble
            }`}
          >
            <Typography component="div" className={classes.messageText}>
              {message.role === "assistant"
                ? renderMarkdown(message.text)
                : message.text}
            </Typography>
            <Typography
              className={`${classes.timestamp} ${
                message.role === "assistant"
                  ? classes.botTimestampColor
                  : classes.userTimestampColor
              }`}
            >
              {message.created_at ? formatTimestamp(message.created_at) : ""}
            </Typography>
          </Box>
        </Box>
      ))}

      {isTyping && <TypingIndicator />}

      <div ref={messagesEndRef} />
    </Box>
  );
};

export default MessageList;
